import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const emptyBusinessInfo = {
  business_description: '',
  payment_method: '',
  return_policy: '',
  purchase_info: '',
  delivery_and_shipping: '',
  contact_info: { email: '', address: '', hours_of_operation: '' },
};

const textAreaClass = 'w-full resize-y rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] px-3 py-2 text-sm text-[var(--tg-text-primary)] outline-none focus:border-indigo-400';
const errorText = (error, fallback) => error?.response?.data?.detail || error?.message || fallback;

const normalizeRecord = (payload) => {
  const value = Array.isArray(payload?.data) ? payload.data[0] : (payload?.data || payload);
  if (!value || Array.isArray(value)) return emptyBusinessInfo;
  return {
    ...emptyBusinessInfo,
    ...value,
    contact_info: { ...emptyBusinessInfo.contact_info, ...(value.contact_info || {}) },
  };
};

const Field = ({ label, hint, children, tour }) => (
  <label data-tour={tour} className="block space-y-2">
    <span className="text-sm font-medium text-[var(--tg-text-primary)]">{label}</span>
    {children}
    {hint && <span className="block text-xs text-[var(--tg-text-muted)]">{hint}</span>}
  </label>
);

const BusinessInfoPanel = ({ apiBase, authConfig, enabled = true }) => {
  const [form, setForm] = useState(emptyBusinessInfo);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiBase}/knowledge/business-info`, authConfig());
      setForm(normalizeRecord(response.data));
    } catch (loadError) {
      if (loadError.response?.status === 404) setForm(emptyBusinessInfo);
      else setError(errorText(loadError, 'Could not load business information from Meta.'));
    } finally {
      setLoading(false);
    }
  }, [apiBase, authConfig, enabled]);

  useEffect(() => { load(); }, [load]);

  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const setContact = (key, value) => setForm((current) => ({
    ...current,
    contact_info: { ...current.contact_info, [key]: value },
  }));

  const save = async () => {
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const response = await axios.put(`${apiBase}/knowledge/business-info`, form, authConfig());
      setForm(normalizeRecord(response.data?.business_description ? response.data : form));
      setNotice('Business information saved in Meta.');
    } catch (saveError) {
      setError(errorText(saveError, 'Could not save business information.'));
    } finally {
      setWorking(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset all Meta Agent business information? This clears the complete record in Meta.')) return;
    setWorking(true);
    setError('');
    setNotice('');
    try {
      await axios.delete(`${apiBase}/knowledge/business-info`, authConfig());
      setForm(emptyBusinessInfo);
      setNotice('Business information reset in Meta.');
    } catch (resetError) {
      setError(errorText(resetError, 'Could not reset business information.'));
    } finally {
      setWorking(false);
    }
  };

  if (!enabled) return <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">Configure the WhatsApp number and server token before managing Agent Knowledge.</div>;
  if (loading) return <div className="flex items-center justify-center py-20 text-[var(--tg-text-muted)]"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading business information...</div>;

  return (
    <section className="space-y-5 rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-indigo-500/15 p-2.5 text-indigo-400"><Building2 className="h-5 w-5" /></div>
          <div><h2 className="text-xl font-semibold text-[var(--tg-text-primary)]">Business information</h2><p className="text-sm text-[var(--tg-text-muted)]">Give the agent factual information about your company, policies and contact details.</p></div>
        </div>
        <div data-tour="business-actions" className="flex gap-2"><Button variant="outline" onClick={reset} disabled={working}><RotateCcw className="mr-2 h-4 w-4" />Reset</Button><Button onClick={save} disabled={working}>{working ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save</Button></div>
      </div>
      <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-xs text-amber-300">Meta treats Save as a full replacement. This form always sends every field shown below so an accidental partial update cannot silently remove another field.</div>
      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
      {notice && <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">{notice}</div>}
      <Field tour="business-description" label="Business description"><textarea rows={5} className={textAreaClass} value={form.business_description} onChange={(event) => setValue('business_description', event.target.value)} placeholder="What the business offers, who it serves, and important facts" /></Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field tour="business-email" label="Contact email"><Input type="email" value={form.contact_info.email} onChange={(event) => setContact('email', event.target.value)} placeholder="support@example.com" /></Field>
        <Field tour="business-address" label="Address"><Input value={form.contact_info.address} onChange={(event) => setContact('address', event.target.value)} placeholder="Business address" /></Field>
      </div>
      <Field tour="business-hours" label="Hours of operation"><textarea rows={3} className={textAreaClass} value={form.contact_info.hours_of_operation} onChange={(event) => setContact('hours_of_operation', event.target.value)} placeholder="Monday-Friday, 9:00 AM-6:00 PM IST" /></Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field tour="business-payments" label="Payment methods"><textarea rows={4} className={textAreaClass} value={form.payment_method} onChange={(event) => setValue('payment_method', event.target.value)} /></Field>
        <Field tour="business-purchase" label="Purchase information"><textarea rows={4} className={textAreaClass} value={form.purchase_info} onChange={(event) => setValue('purchase_info', event.target.value)} /></Field>
        <Field tour="business-delivery" label="Delivery and shipping"><textarea rows={4} className={textAreaClass} value={form.delivery_and_shipping} onChange={(event) => setValue('delivery_and_shipping', event.target.value)} /></Field>
        <Field tour="business-return" label="Return policy"><textarea rows={4} className={textAreaClass} value={form.return_policy} onChange={(event) => setValue('return_policy', event.target.value)} /></Field>
      </div>
    </section>
  );
};

export default BusinessInfoPanel;
